import React, { memo } from 'react';
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { LockRounded, LockOpenRounded, MoveDownRounded, PersonOutline, GroupsOutlined } from '@mui/icons-material';
import type { Entry } from '../../../services/timetableService';
import type { BoardIndex } from './boardModel';
import { DAY_NAMES, gridColumns } from '../timetableUtils';

interface EntryMenuProps {
  anchor: HTMLElement | null;
  entry: Entry | null;
  index: BoardIndex;
  editable: boolean;
  onClose: () => void;
  onLock: (entry: Entry, locked: boolean) => void;
  onUnplace: (entry: Entry) => void;
  onShowTeacher: (teacherId: string) => void;
  onShowClass: (entry: Entry) => void;
}

/** One period's details and what can be done with it. */
const EntryMenu = memo<EntryMenuProps>(({ anchor, entry, index, editable, onClose, onLock, onUnplace, onShowTeacher, onShowClass }) => {
  if (!entry) return null;
  const grid = index.board.timetable!.grid;
  const times = gridColumns(grid).filter((c) => c.kind === 'period') as Extract<ReturnType<typeof gridColumns>[number], { kind: 'period' }>[];
  const from = times[entry.period - 1];
  const to = times[entry.period + entry.span - 2];
  const room = index.roomName(entry.roomId);
  const act = (fn: () => void) => () => { fn(); onClose(); };

  return (
    <Menu anchorEl={anchor} open={!!anchor} onClose={onClose} slotProps={{ paper: { sx: { minWidth: 250, borderRadius: '12px' } } }}>
      <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
        <Typography variant="subtitle2" fontWeight={700}>{index.subjectName(entry.subjectId)}{entry.span === 2 ? ' · double' : ''}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {index.sectionLabelOf(entry)} · {index.teacherName(entry.teacherId)}{room ? ` · ${room}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {DAY_NAMES[entry.day]}, {from?.start}–{to?.end}
        </Typography>
      </Box>
      <Divider />
      {editable && (
        <MenuItem onClick={act(() => onLock(entry, !entry.locked))}>
          <ListItemIcon>{entry.locked ? <LockOpenRounded fontSize="small" /> : <LockRounded fontSize="small" />}</ListItemIcon>
          <ListItemText primary={entry.locked ? 'Unlock' : 'Lock in place'} secondary={entry.locked ? undefined : 'Generating again won’t move it'} />
        </MenuItem>
      )}
      {editable && (
        <MenuItem onClick={act(() => onUnplace(entry))}>
          <ListItemIcon><MoveDownRounded fontSize="small" /></ListItemIcon>
          <ListItemText primary="Take off the week" secondary="Back to unplaced" />
        </MenuItem>
      )}
      {entry.teacherId && (
        <MenuItem onClick={act(() => onShowTeacher(entry.teacherId!))}>
          <ListItemIcon><PersonOutline fontSize="small" /></ListItemIcon>
          <ListItemText primary={`${index.teacherName(entry.teacherId)}’s week`} />
        </MenuItem>
      )}
      <MenuItem onClick={act(() => onShowClass(entry))}>
        <ListItemIcon><GroupsOutlined fontSize="small" /></ListItemIcon>
        <ListItemText primary={`${index.sectionLabelOf(entry)}’s week`} />
      </MenuItem>
    </Menu>
  );
});

EntryMenu.displayName = 'EntryMenu';

export default EntryMenu;
